/** @format */

import { useState, useCallback, useEffect } from "react";
import "./App.css";
import { SearchForm } from "./components/SearchForm";
import { QuestionList } from "./components/QuestionList";
import { FacetPanel } from "./components/FacetPanel";
import { SearchResponse, SearchParams, Brand, Aggregations } from "@question-bank/shared";
import { UILanguage } from "./components/LanguageToggle";

import { searchQuestions } from "./api/questions";
import { useAuth } from "./hooks/useAuth";
import { useTranslation } from "react-i18next";
import i18n from "./features/i18n/init";
import { Header } from "./components/Header/Header";

import { notify } from "./components/Toast/toast";

const emptyAggregations: Aggregations = {
	subject: [],
	level: [],
	tier: [],
};

/** Filter keys we persist in the URL */
const URL_PARAM_KEYS: (keyof SearchParams)[] = ["subject", "level", "tier", "season", "year", "paperCode", "offset"];

function readUrlParams(): { brand: Brand; language: UILanguage; searchParams: SearchParams } {
	const url = new URL(globalThis.location.href);
	const rawBrand = url.searchParams.get("brand");
	const brand: Brand = rawBrand === "eduqas" ? "eduqas" : "wjec";
	const rawLanguage = url.searchParams.get("language");
	const requestedLanguage: UILanguage = rawLanguage === "cy" ? "cy" : "en";
	const language: UILanguage = brand === "eduqas" ? "en" : requestedLanguage;

	const searchParams: SearchParams = {
		language,
		limit: 20,
		offset: 0,
	};

	for (const key of URL_PARAM_KEYS) {
		const val = url.searchParams.get(key);
		if (val) {
			(searchParams as Record<string, unknown>)[key] =
				key === "offset" || key === "limit" || key === "year" ? Number(val) : val;
		}
	}

	return { brand, language, searchParams };
}

function writeUrlParams(brand: Brand, language: UILanguage, params: SearchParams) {
	const url = new URL(globalThis.location.href);

	// Clear all managed keys first
	url.searchParams.delete("brand");
	url.searchParams.delete("language");
	for (const key of URL_PARAM_KEYS) {
		url.searchParams.delete(key);
	}

	// Only write non-default values
	if (brand !== "wjec") {
		url.searchParams.set("brand", brand);
	}
	if (language !== "en") {
		url.searchParams.set("language", language);
	}

	for (const key of URL_PARAM_KEYS) {
		const val = params[key];
		if (val !== undefined && val !== null && val !== "" && val !== 0) {
			url.searchParams.set(key, String(val));
		}
	}

	globalThis.history.replaceState(null, "", url.toString());
}

function App() {
	const initial = readUrlParams();
	const { t } = useTranslation();
	const [brand] = useState<Brand>(initial.brand);
	const [language] = useState<UILanguage>(initial.language);
	const [searchParams, setSearchParams] = useState<SearchParams>(initial.searchParams);
	const [results, setResults] = useState<SearchResponse | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const { error: authError } = useAuth();

	// sync i18next language whenever React language state changes
	useEffect(() => {
		void i18n.changeLanguage(language);
	}, [language]);

	// Perform search
	const performSearch = useCallback(
		async (params: SearchParams) => {
			setIsLoading(true);
			setError(null);

			try {
				const response = await searchQuestions(brand, params);
				setResults(response);
			} catch (err) {
				setError(err instanceof Error ? err.message : "Search failed");
				setResults(null);
			} finally {
				setIsLoading(false);
			}
		},
		[brand],
	);

	// Initial search on load
	useEffect(() => {
		performSearch(searchParams);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [brand, language]); // Re-search when brand or language changes

	// Sync URL whenever search state changes
	useEffect(() => {
		writeUrlParams(brand, language, searchParams);
	}, [brand, language, searchParams]);

	// Handle search submit
	const handleSearch = (params: SearchParams) => {
		const newParams: SearchParams = {
			...params,
			language, // Always apply the selected language
			offset: 0, // Reset pagination on new search
		};
		setSearchParams(newParams);
		performSearch(newParams);
	};

	// Handle facet selection
	const handleFacetClick = (field: string, value: string) => {
		const newParams: SearchParams = {
			...searchParams,
			[field]: searchParams[field as keyof SearchParams] === value ? undefined : value,
			offset: 0,
		};
		setSearchParams(newParams);
		performSearch(newParams);
	};

	// Handle pagination
	const handlePageChange = (offset: number) => {
		const newParams: SearchParams = {
			...searchParams,
			offset,
		};
		setSearchParams(newParams);
		performSearch(newParams);
	};

	useEffect(() => {
		if (authError) {
			notify("error", authError);
		}
	}, [authError]);

	return (
		<div className="app">
			<Header appTitle={t("common.appTitle")} />
			<main className="app-main">
				<aside className="app-sidebar">
					<FacetPanel aggregations={results?.aggregations || emptyAggregations} onFilterClick={handleFacetClick} />
				</aside>

				<section className="app-content">
					<SearchForm
						onSearch={handleSearch}
						isLoading={isLoading}
						aggregations={results?.aggregations || emptyAggregations}
						selectedSubject={searchParams.subject}
						selectedLevel={searchParams.level}
						selectedTier={searchParams.tier}
					/>

					{error && (
						<div className="error-banner">
							<span className="error-icon">⚠️</span>
							<span>{error}</span>
						</div>
					)}

					<QuestionList
						questions={results?.data ?? []}
						total={results?.meta.total || 0}
						limit={searchParams.limit || 20}
						offset={searchParams.offset || 0}
						isLoading={isLoading}
						onPageChange={handlePageChange}
					/>
				</section>
			</main>

			<footer className="app-footer">
				<p>
					<span>Connected to {brand.toUpperCase()} API</span>
				</p>
			</footer>
		</div>
	);
}

export default App;
